import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Login, LoginResponse, SessionUser } from '@shared/models';
import { CookieService } from '@shared/services/cookie.service';
import { UserPermissionsService } from '@shared/services/user-permissions.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly SESSION_COOKIE_NAME = 'session_user';

  private readonly API = `${environment.baseUrl}/auth`;
  readonly currentUser = signal<SessionUser | null>(null);

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private userPermissionsService: UserPermissionsService,
  ) {
    this.currentUser.set(this.getStoredSession());
  }

  login(credentials: Login): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.API}/login`, credentials);
  }

  refreshToken(): Observable<LoginResponse> {
    const refreshToken = this.getRefreshToken();
    return this.http.post<LoginResponse>(`${this.API}/refresh-token`, { 
        refresh_token: refreshToken 
    }).pipe(
        tap(response => this.startSession(response))
    );
}

  startSession(response: LoginResponse): void {
  this.cookieService.setCookie('access_token', response.access_token, 1); 

  if (response.refresh_token) {
    this.cookieService.setCookie('refresh_token', response.refresh_token, 1);
  }

  const sessionUser: SessionUser = {
    id: response.id,
    email: response.email,
    name: response.name,
    group: response.group
  };

  this.cookieService.setJsonCookie(this.SESSION_COOKIE_NAME, sessionUser, 1);
  this.currentUser.set(sessionUser);
}
  logout(refresh_token: string): Observable<void> {
    return this.http.post<void>(`${this.API}/logout`, { refresh_token });
  }

  clearSession(): void {
    this.cookieService.deleteCookie('access_token');
    this.cookieService.deleteCookie('token');
    this.cookieService.deleteCookie('refresh_token');
    this.cookieService.deleteCookie(this.SESSION_COOKIE_NAME);
    this.currentUser.set(null);
    this.userPermissionsService.clear();
  }

  updateProfileImageUrl(url: string | null): void {
    const current = this.currentUser();
    if (!current) return;
    const updated: SessionUser = { ...current, profileImageUrl: url };
    this.cookieService.setJsonCookie(this.SESSION_COOKIE_NAME, updated, 1);
    this.currentUser.set(updated);
  }

  getToken(): string {
    return this.cookieService.getCookie('access_token')
      ?? this.cookieService.getCookie('token')
      ?? '';
  }

  getRefreshToken(): string {
    return this.cookieService.getCookie('refresh_token') ?? '';
  }

  isAuthenticated(): boolean {
    return !!this.currentUser()?.id || !!this.getToken();
  }

  getUserPermissions(): Observable<string[]> {
    return of(this.userPermissionsService.roles());
  }

  private getStoredSession(): SessionUser | null {
    return this.cookieService.getJsonCookie<SessionUser>(this.SESSION_COOKIE_NAME);
  }
}
